import { BLOCK_PARAGRAPH, heading } from '@gitbook/doc-core';
import type { Block, Document } from '@gitbook/slate';
import { List } from '@gitbook/util-immutable';

import type { ParsingNodeIndexes } from '../context';
import { findMostStablePartOfMarkdown, roundToNextLine } from './partial';

export type MarkdownIncrementalFrontmatterResult<Attributes = object> = {
    attributes: Attributes;
    bodyBegin: number;
};

export type MarkdownIncrementalCacheState = {
    /** Frontmatter previous parsed */
    frontmatter: MarkdownIncrementalFrontmatterResult<any> | null;
    /** Index where to start next parsing safely from within the body */
    endIndex: number;
    /** Blocks parsed until `endIndex` */
    blocks: List<Block>;
};

/**
 * Get the incremental cache state for the given source and document.
 * It can be passed to a next call to parseMarkdown to continue parsing from the last known safe state.
 *
 * IMPORTANT: The cache is only valid for the same text. If the text is different, the cache will be invalidated.
 */
export function getIncrementalCacheState({
    frontmatter,
    partialSource,
    partialDocument,
    nodeIndexes,
    previousIncrementalCacheState,
    maxIncrementalOffset,
}: {
    /** Frontmatter extracted from the source */
    frontmatter: MarkdownIncrementalFrontmatterResult<any>;
    /** Partial source parsed */
    partialSource: string;
    /** Partial document parsed from the source */
    partialDocument: Document;
    /** Node indexes for the partial document. */
    nodeIndexes: ParsingNodeIndexes;
    /** Previous incremental cache state  */
    previousIncrementalCacheState: MarkdownIncrementalCacheState | null;
    /** Maximum offset to use for incremental parsing. */
    maxIncrementalOffset: number;
}): MarkdownIncrementalCacheState | null {
    if (partialDocument.nodes.size < 2) {
        return previousIncrementalCacheState;
    }

    // Maximum index we can safely parse from the partial source.
    const maxPartialInputIndex = findMostStablePartOfMarkdown(partialSource, true);

    // Maximum index we can safely output in the cache from the full source
    const maxInputIndex = Math.min(
        (previousIncrementalCacheState?.endIndex ?? 0) + maxPartialInputIndex,
        maxIncrementalOffset
    );

    // Iterate over the blocks in reverse order to find the last safe block.
    for (let blockIndex = partialDocument.nodes.size - 2; blockIndex >= 0; blockIndex--) {
        const block = partialDocument.nodes.get(blockIndex);
        if (!block) {
            continue;
        }

        const position = nodeIndexes.get(block.key);
        if (!position) {
            continue;
        }

        if (block.type !== BLOCK_PARAGRAPH && !heading.isHeading(block)) {
            continue;
        }

        const endPartialIndex = position[1];
        const endBlockPartialIndex = roundToNextLine(partialSource, endPartialIndex);

        if (endBlockPartialIndex === 0) {
            continue;
        }

        const endIndex = (previousIncrementalCacheState?.endIndex ?? 0) + endBlockPartialIndex;
        if (endIndex > maxInputIndex) {
            // We've reached the end of the safe blocks.
            continue;
        }

        return {
            frontmatter,
            endIndex,
            blocks: (previousIncrementalCacheState
                ? previousIncrementalCacheState.blocks
                : List.create<Block>()
            ).concat(partialDocument.nodes.slice(0, blockIndex + 1)),
        };
    }

    return previousIncrementalCacheState;
}
